import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import { RootState } from '../../../../core/state/state';
import { changeProjectStatus, getProjects } from '../../../../core/state/project/asyncActions';
import { ProjectStatus } from '../../../../core/state/project/types';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { NotificationPosition } from '../../../../core/state/global-notifications/types';

interface Props {
  isOpen: boolean;
  projectId: number;
  closeModal: () => void;
  isActive: boolean;
}

const ProjectActivationModal: React.FC<Props> = ({ isOpen, closeModal, projectId, isActive }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedProject = useSelector((state: RootState) => state.projects.projects?.find((project) => project.id === projectId));
  const projectName = selectedProject?.name;

  const activateProject = async () => {
    await dispatch(changeProjectStatus(projectId, isActive ? ProjectStatus.Archived : ProjectStatus.Active));
    if (isActive) {
      dispatch(
        spawnGlobalNotification({
          type: GlobalNotificationTypes.Success,
          message: t('projectManagement.modal.archive.success.message', { projectName }),
          title: t('projectManagement.modal.archive.success.title'),
          postition: NotificationPosition.Top
        })
      );
    }
    await dispatch(getProjects());
    closeModal();
  };

  const getMessages = () => {
    if (isActive && selectedProject?.hasLinkedBarrierDevice) {
      return { title: `${t('common.attention')}!`, content: t('projectManagement.modal.archive.warning.text') };
    }
    if (isActive && !selectedProject?.hasLinkedBarrierDevice) {
      return { title: t('projectManagement.modal.archive.title'), content: t('projectManagement.modal.archive.text', { projectName }) };
    }
    return { title: t('projectManagement.modal.activate.title'), content: t('projectManagement.modal.activate.text', { projectName }) };
  };

  const title = <h3 className="modal-title">{getMessages().title}</h3>;

  const buttons = (
    <div className="d-flex justify-content-end">
      <Button buttonTypes={['transparent']} onClick={closeModal}>
        {t('common.cancel')}
      </Button>
      <Button onClick={() => activateProject()} buttonTypes={['blue']}>
        {t('common.confirm')}
      </Button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} title={title} toolbar={buttons} size="small">
      <span>{getMessages().content}</span>
    </Modal>
  );
};

export default ProjectActivationModal;
